import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function toNumber(value) {
    const number = parseFloat(value || 0);
    return Number.isFinite(number) ? number : 0;
}

function NumberField({ label, value, onChange, placeholder, testId }) {
    return (
        <div>
            <Label>{label}</Label>
            <Input
                type="number"
                step="0.1"
                value={value || ""}
                onChange={(e) => onChange(toNumber(e.target.value))}
                placeholder={placeholder}
                data-testid={testId}
            />
        </div>
    );
}

function TextField({ label, value, onChange, placeholder, type = "text" }) {
    return (
        <div>
            <Label>{label}</Label>
            <Input
                type={type}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
            />
        </div>
    );
}

export default function SetbackVerification({ site, setSite }) {
    return (
        <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/70 p-4">
            <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Setback verification</div>
                <div className="mt-1 text-xs text-slate-500">
                    Track permit setbacks here. These fields are verification records, not auto-trusted quote calculations.
                </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
                <NumberField label="Front ft" value={site.setback_front_ft} onChange={(v) => setSite({ setback_front_ft: v })} placeholder="Verify" testId="step-site-setback-front-input" />
                <NumberField label="Rear ft" value={site.setback_rear_ft} onChange={(v) => setSite({ setback_rear_ft: v })} placeholder="Verify" />
                <NumberField label="Left side ft" value={site.setback_left_ft} onChange={(v) => setSite({ setback_left_ft: v })} placeholder="Verify" />
                <NumberField label="Right side ft" value={site.setback_right_ft} onChange={(v) => setSite({ setback_right_ft: v })} placeholder="Verify" />
                <NumberField label="Corner side ft" value={site.setback_corner_side_ft} onChange={(v) => setSite({ setback_corner_side_ft: v })} placeholder="If applicable" />
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                    <Label>Verification status</Label>
                    <Select value={site.setback_verification_status || "needs_verification"} onValueChange={(v) => setSite({ setback_verification_status: v })}>
                        <SelectTrigger data-testid="step-site-setback-status-select">
                            <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="needs_verification">Needs verification</SelectItem>
                            <SelectItem value="in_review">In review</SelectItem>
                            <SelectItem value="verified">Verified</SelectItem>
                            <SelectItem value="manual_override">Manual override</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div>
                    <Label>Confidence</Label>
                    <Select value={site.setback_confidence_level || "low"} onValueChange={(v) => setSite({ setback_confidence_level: v })}>
                        <SelectTrigger>
                            <SelectValue placeholder="Select confidence" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <TextField type="date" label="Verified date" value={site.setback_verified_at} onChange={(v) => setSite({ setback_verified_at: v })} />
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <TextField label="Zoning / source name" value={site.setback_source_name} onChange={(v) => setSite({ setback_source_name: v })} placeholder="County, city, GIS, zoning code, survey, etc." />
                <TextField label="Source section / zoning note" value={site.setback_source_section} onChange={(v) => setSite({ setback_source_section: v })} placeholder="Example: R-1, MH, zoning table, section number" />
                <TextField label="Source URL" value={site.setback_source_url} onChange={(v) => setSite({ setback_source_url: v })} placeholder="Optional link to GIS/code/source" />
                <TextField label="Verified by" value={site.setback_verified_by} onChange={(v) => setSite({ setback_verified_by: v })} placeholder="Team member or jurisdiction contact" />
            </div>

            <TextField label="Setback notes" value={site.setback_notes} onChange={(v) => setSite({ setback_notes: v })} placeholder="Lot type, easements, road frontage, community rules, special conditions, permit notes" />

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                Setbacks should remain marked Needs Verification until zoning, parcel records, lot orientation, easements, and any community rules are confirmed.
            </div>
        </div>
    );
}
